import { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSubscriptionStatus, cancelPayment, setPaymentSuccessData } from "../store/billingSlice";
import { fetchCapabilitiesRequest } from "../store/capabilitiesSlice";
import { showAlert } from "../store/alertSlice";
import { clearCapabilitiesFromStorage } from "../utils/capabilitiesStorage";
import { getPastTransactions } from "../store/invoiceSlice";

export const usePaymentHandlers = ({
  billing,
  selectedPlanDetails,
  setSubscriptionStatus,
  setCurrentView,
  setIsProcessingPayment,
}) => {
  const dispatch = useDispatch();
  const invoice = useSelector((state) => state.invoice);
  const pollingAttempts = useRef(0);
  const pollTimerRef = useRef(null);
  const isPollingRef = useRef(false);
  const hasCalledSuccessRef = useRef(false);
  const hasFetchedTransactionsRef = useRef(false);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    isPollingRef.current = false;
  };

  const triggerPaymentSuccess = (invoiceId = null) => {
    if (hasCalledSuccessRef.current) return;
    hasCalledSuccessRef.current = true;

    stopPolling();
    clearCapabilitiesFromStorage();
    dispatch(fetchCapabilitiesRequest({
      shouldPoll: true,
      pollUntil: 'activePlan',
      expectedPlanId: selectedPlanDetails?.id,
      silent: true,
    }));
    setSubscriptionStatus("active");
    setIsProcessingPayment(false);

    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    const paymentData = {
      invoiceNumber: `INV-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`,
      datePaid: formattedDate,
      planName: selectedPlanDetails?.name || 'Selected Plan',
      amount: selectedPlanDetails?.basePrice?.toFixed(2) || '0.00',
      gst: selectedPlanDetails?.gstAmount?.toFixed(2) || '0.00',
      totalAmount: selectedPlanDetails?.totalWithGst?.toFixed(2) || '0.00',
      invoiceId: invoiceId,
    };

    dispatch(setPaymentSuccessData(paymentData));
  };

  useEffect(() => {
    if (billing.errorSubscriptionStatus && isPollingRef.current) {
      stopPolling();
      setIsProcessingPayment(false);
      dispatch(showAlert({ message: "Your payment was received. Your subscription will be activated shortly. You will receive a confirmation email.", type: "info" }));
    }
  }, [billing.errorSubscriptionStatus, dispatch, setIsProcessingPayment]);

  useEffect(() => {
    if (billing.subscription && isPollingRef.current) {
      const status = billing.subscription?.status;
      
      if (status === 'ACTIVE' && !hasFetchedTransactionsRef.current) {
        hasFetchedTransactionsRef.current = true;
        dispatch(getPastTransactions());
      }
    }
  }, [billing.subscription?.status, dispatch]);

  useEffect(() => {
    if (hasFetchedTransactionsRef.current && isPollingRef.current && invoice.transactions && invoice.transactions.length > 0) {
      const firstTransaction = invoice.transactions[0];
      const invoiceId = firstTransaction?.invoiceId;
      triggerPaymentSuccess(invoiceId);
      hasFetchedTransactionsRef.current = false;
    }
  }, [invoice.transactions, dispatch]);

  const handlePaymentSuccess = (razorpayResponse) => {
    setIsProcessingPayment(true);
    pollingAttempts.current = 0;
    isPollingRef.current = true;
    hasCalledSuccessRef.current = false;
    hasFetchedTransactionsRef.current = false;

    const subscriptionId = billing.subscription?.subscriptionId;
    if (!subscriptionId) {
      dispatch(showAlert({ message: "Subscription ID not found. Please contact support.", type: "error" }));
      setIsProcessingPayment(false);
      isPollingRef.current = false;
      return;
    }

    const pollSubscription = () => {
      if (!isPollingRef.current) return; // stop if cancelled

      pollingAttempts.current += 1;
      if (pollingAttempts.current > 20) {
        stopPolling();
        dispatch(showAlert({ message: "Your payment was received. Your subscription will be activated shortly. You will receive a confirmation email.", type: "info" }));
        setIsProcessingPayment(false);
        triggerPaymentSuccess();
        return;
      }

      dispatch(getSubscriptionStatus({ subscriptionId }));
      pollTimerRef.current = setTimeout(pollSubscription, 3000);
    };

    pollSubscription();
  };

  const handlePaymentDismissed = () => {
    stopPolling();

    const subscriptionId = billing.subscription?.subscriptionId;
    if (subscriptionId) {
      dispatch(cancelPayment({ subscriptionId }));
    } else {
      dispatch(showAlert({ message: "Payment cancelled. You can try again anytime.", type: "info" }));
    }

    setCurrentView("plans");
  };

  const handlePaymentFailure = (error) => {
    stopPolling();
    dispatch(showAlert({ message: error?.description || "Payment failed. Please try again.", type: "error" }));
    setCurrentView("plans");
  };

  return {
    handlePaymentSuccess,
    handlePaymentDismissed,
    handlePaymentFailure,
  };
};